import { BettingStructure } from '@/services/common/types/bettingStructure';
import { PokerCategory } from '@/services/common/types/pokerCategory';
import { ReactComponent as All } from '../../../assets/game-categories/all.svg';
import { ReactComponent as Holdem } from '../../../assets/game-categories/holdem.svg';
import { ReactComponent as Omaha } from '../../../assets/game-categories/omaha.svg';
import { ReactComponent as Stud } from '../../../assets/game-categories/stud.svg';
import { ReactComponent as Draw } from '../../../assets/game-categories/draw.svg';
import { ReactComponent as Mixed } from '../../../assets/game-categories/mixed.svg';
import { ReactComponent as Chinese } from '../../../assets/game-categories/chinese.svg';
import { ReactComponent as Custom } from '../../../assets/game-categories/custom.svg';
import { ReactComponent as AllActive } from '../../../assets/game-categories/all-active.svg';
import { ReactComponent as HoldemActive } from '../../../assets/game-categories/holdem-active.svg';
import { ReactComponent as OmahaActive } from '../../../assets/game-categories/omaha-active.svg';
import { ReactComponent as StudActive } from '../../../assets/game-categories/stud-active.svg';
import { ReactComponent as DrawActive } from '../../../assets/game-categories/draw-active.svg';
import { ReactComponent as MixedActive } from '../../../assets/game-categories/mixed-active.svg';
import { ReactComponent as ChineseActive } from '../../../assets/game-categories/chinese-active.svg';
import { ReactComponent as CustomActive } from '../../../assets/game-categories/custom-active.svg';
import { FunctionComponent, SVGProps } from 'react';
import { GameValue, GameVariants } from '../filters/filters';
import { currencySymbol } from '@/common/utils/utils';

export type StakeName = 'Micro' | 'Medium' | 'High';

interface StakeOption {
  stakeName: StakeName;
  range: {
    min: number;
    max: number | undefined;
    text: string;
  };
}

export interface StakesOptionsBet {
  bettingStructure: BettingStructure;
  stakes: StakeOption[];
}

export const stakesOptions: StakesOptionsBet[] = [
  {
    bettingStructure: BettingStructure.NO_LIMIT,
    stakes: [
      {
        stakeName: 'Micro',
        range: { min: 0, max: 1, text: `${currencySymbol()}0/1` },
      },
      {
        stakeName: 'Medium',
        range: { min: 1, max: 10, text: `${currencySymbol()}1/10` },
      },
      {
        stakeName: 'High',
        range: { min: 10, max: undefined, text: `${currencySymbol()}10 and Higher` },
      },
    ],
  },
  {
    bettingStructure: BettingStructure.POT_LIMIT,
    stakes: [
      {
        stakeName: 'Micro',
        range: { min: 0, max: 1, text: `${currencySymbol()}0/1` },
      },
      {
        stakeName: 'Medium',
        range: { min: 1, max: 10, text: `${currencySymbol()}1/10` },
      },
      {
        stakeName: 'High',
        range: { min: 10, max: undefined, text: `${currencySymbol()}10 and Higher` },
      },
    ],
  },
  {
    bettingStructure: BettingStructure.FIXED_LIMIT,
    stakes: [
      {
        stakeName: 'Micro',
        range: { min: 0, max: 8, text: `${currencySymbol()}0/8` },
      },
      {
        stakeName: 'Medium',
        range: { min: 5, max: 80, text: `Up to ${currencySymbol()}5/80` },
      },
      {
        stakeName: 'High',
        range: { min: 50, max: undefined, text: `${currencySymbol()}50 and Higher` },
      },
    ],
  },
];

export const buyInOptions: {
  min: number;
  max: number;
  text: string;
}[] = [
  { min: 500, max: 20000, text: `${currencySymbol()}500/20K` },
  { min: 2000, max: 50000, text: `${currencySymbol()}2K/50K` },
  { min: 5000, max: 100000, text: `${currencySymbol()}5K/100K` },
  { min: 10000, max: 250000, text: `${currencySymbol()}10K/250K` },
  { min: 100000, max: 500000, text: `${currencySymbol()}100K/500k` },
  { min: 250000, max: 1000000, text: `${currencySymbol()}250K/1M` },
  { min: 1000000, max: 5000000, text: `${currencySymbol()}1M/5M` },
  { min: 2000000, max: 10000000, text: `${currencySymbol()}2M/10M` },
  { min: 5000000, max: 50000000, text: `${currencySymbol()}5M/50M` },
];

export const rakeOptions: number[] = [5, 6, 7, 8];

export const categoryOptions: {
  title: string;
  category: PokerCategory;
  image: FunctionComponent<SVGProps<SVGSVGElement>>;
  activeImage: FunctionComponent<SVGProps<SVGSVGElement>>;
  margin?: number;
  comingSoon?: boolean;
}[] = [
  {
    title: 'ALL',
    category: PokerCategory.ALL,
    image: All,
    activeImage: AllActive,
    margin: -1,
  },
  {
    title: "HOLD'EM",
    category: PokerCategory.HOLDEM,
    image: Holdem,
    activeImage: HoldemActive,
  },
  {
    title: 'OMAHA',
    category: PokerCategory.OMAHA,
    image: Omaha,
    activeImage: OmahaActive,
  },
  {
    title: 'STUD',
    category: PokerCategory.STUD,
    image: Stud,
    activeImage: StudActive,
  },
  {
    title: 'DRAW',
    category: PokerCategory.DRAW,
    image: Draw,
    activeImage: DrawActive,
  },
  {
    title: 'MIXED',
    category: PokerCategory.MIXED,
    image: Mixed,
    activeImage: MixedActive,
  },
  {
    title: 'CHINESE',
    category: PokerCategory.CHINESE,
    image: Chinese,
    activeImage: ChineseActive,
    comingSoon: true,
  },
  {
    title: 'CUSTOM',
    category: PokerCategory.CUSTOM,
    image: Custom,
    activeImage: CustomActive,
    comingSoon: true,
  },
];

export const tableFeaturesOptions: { title: string; feature: string }[] = [
  { title: 'Ante', feature: 'Ante' },
  { title: 'Straddle', feature: 'Straddle' },
  { title: 'Double Board', feature: 'Double Board' },
  { title: 'Kill Pots', feature: 'Kill Pots' },
  { title: 'Capped Game', feature: 'Capped Game' },
  { title: 'Uncapped Buy In', feature: 'Uncapped Buy In' },
];

export const tableSize: number[] = [2, 6, 7, 8, 9];

export const gameOptions: GameValue[] = [
  {
    category: PokerCategory.HOLDEM,
    label: 'Holdem',
    games: [GameVariants.NoLimitTexasHoldem, GameVariants.NoLimitShortDeckHoldem, GameVariants.FixedLimitTexasHoldem],
  },
  {
    category: PokerCategory.OMAHA,
    label: 'Omaha',
    games: [GameVariants.PotLimitOmaha, GameVariants.PotLimitOmahaCapped, GameVariants.PotLimitOmahaEightOrBetter],
  },
  {
    category: PokerCategory.OMAHA,
    label: '5 card Omaha',
    games: [
      GameVariants.PotLimitFiveCardOmaha,
      GameVariants.PotLimitFiveCardOmahaCapped,
      GameVariants.PotLimitFiveCardOmahaEightOrBetter,
      GameVariants.PotLimitFiveCardOmahaEightOrBetterCapped,
      GameVariants.FixedLimitFiveCardOmahaEightOrBetter,
    ],
  },
  {
    category: PokerCategory.STUD,
    label: 'Stud',
    games: [
      GameVariants.FixedLimitSevenCardStud,
      GameVariants.FixedLimitStudEightOrBetter,
      GameVariants.FixedLimitStudHighLowRegular,
    ],
  },
  {
    category: PokerCategory.STUD,
    label: 'Razz',
    games: [GameVariants.FixedLimitRazz, GameVariants.FixedLimitDeuceToSevenRazz],
  },
  {
    category: PokerCategory.DRAW,
    label: 'Badugi',
    games: [GameVariants.FixedLimitBadugi],
  },
  {
    category: PokerCategory.DRAW,
    label: 'Badeucy',
    games: [GameVariants.FixedLimitBadeucy],
  },
  {
    category: PokerCategory.DRAW,
    label: 'Badacey',
    games: [GameVariants.FixedLimitBadacey],
  },
  {
    category: PokerCategory.DRAW,
    label: 'A-5 Triple Draw',
    games: [GameVariants.FixedLimitAceToFiveLowballTripleDraw],
  },
  {
    category: PokerCategory.DRAW,
    label: '2-7 Triple Draw',
    games: [GameVariants.FixedLimitDeuceToSevenLowballTripleDraw],
  },
  {
    category: PokerCategory.DRAW,
    label: '2-7 Single Draw',
    games: [GameVariants.NoLimitDeuceToSevenLowballSingleDrawCapped, GameVariants.NoLimitDeuceToSevenLowballSingleDraw],
  },
  {
    category: PokerCategory.DRAW,
    label: 'Archie',
    games: [GameVariants.FixedLimitArchieSixesHighEightOrBetter, GameVariants.FixedLimitArchieNinesHighEightOrBetter],
  },
  {
    category: PokerCategory.DRAW,
    label: 'Dramaha',
    games: [GameVariants.FixedLimitDramahaBadugi, GameVariants.FixedLimitDramahaHigh],
  },
];

export const gameType: BettingStructure[] = [
  BettingStructure.NO_LIMIT,
  BettingStructure.POT_LIMIT,
  BettingStructure.FIXED_LIMIT,
];

export const gameStakes: StakeName[] = ['Micro', 'Medium', 'High'];
